/**
 * THE FIXTURE CALENDAR — what the screen runs against until `/v1/calendar` answers.
 *
 * ★★ THE EVENT SET IS DELIBERATELY THIN, AND THAT IS THE POINT. `calendar_event_kind` holds
 * exactly TWO rows on this platform: `lunar_phase` (family `computed_astro`, seeded by the
 * ephemeris) and `editorial_moment` (family `editorial`, written by an operator) —
 * `prisma/migrations/0016_ruled_policy_values/migration.sql` and
 * `prisma/migrations/0023_sheet_rulings/migration.sql`, and `0023` states the live
 * count in terms: *"calendar_event_kind holds one row, `lunar_phase`"*, before 0023 adds the
 * second. There is no eclipse kind, no ingress kind and no holiday row, which is why
 * `test/acceptance/calendar-a7.spec.ts` carries a `[todo-owner]` about an eclipse it cannot seed.
 * A fixture full of eclipses and ingresses would teach this screen a calendar the platform does not
 * have.
 *
 * ★ ALL TIMESTAMPS ARE FIXED STRINGS. Partly C11 — `eslint.config.mjs` bans zero-argument
 * `new Date()` across `web/` — and partly because a fixture that moves with the wall clock is a
 * fixture whose screenshots never match twice, which is the reason
 * `features/application/api/fixture.ts` gives for the same choice.
 *
 * ★ THE MONTH IS SEPTEMBER 2026 AND THE SUGGESTIONS POINT AT IT. `getPublishDateSuggestions` is how
 * this screen finds its opening month (see `../domain/month.ts`), so the fixture's suggestion list
 * has to be consistent with its event list or the fixture-backed screen opens on an empty month.
 *
 * WHAT IT DOES NOT PRETEND TO BE. It is not a server: it does not check a token, it does not cap the
 * range against `calendar_policy.picker_horizon_days`, and it answers any window by filtering the
 * same seeded rows. It DOES refuse an inverted or empty range, because those two refusals are the
 * whole of the operation's 400 and a screen that has never seen one has never been finished
 * (`src/modules/editorial/cabinet/author-calendar.service.ts`).
 */
import { ApiProblem } from '../../cabinet/api';
import type { OperationQuery } from '../../cabinet/api';
import type {
  AuthorCalendar,
  AuthorCalendarArticle,
  AuthorCalendarDayLoad,
  AuthorCalendarEvent,
  CalendarApi,
  CalendarGroup,
  CalendarGroupList,
  PublishDateSuggestions,
} from './calendar-api';

/**
 * Four moon phases and one editorial moment, over September 2026.
 *
 * `third_quarter`, NEVER `last_quarter`: the schema fixes the spelling by compatibility because a
 * client's decode depends on it (`authors-api.yaml`). `endsAt: null` means an INSTANT and
 * not a zero-length interval — there is no separate flag for it (`:5316-5318`) — and a lunar phase
 * is an instant by `calendar_event_kind.is_interval = false`
 * (`prisma/migrations/0016_ruled_policy_values/migration.sql`).
 */
const EVENTS: readonly AuthorCalendarEvent[] = [
  {
    calendarEventId: 'cev_2026_09_third_quarter',
    kind: 'lunar_phase',
    family: 'computed_astro',
    title: 'Third-quarter moon',
    slug: 'third-quarter-moon-september-2026',
    summary: 'The waning half. Traditionally a week for finishing rather than starting.',
    startsAt: '2026-09-03T22:51:00.000Z',
    endsAt: null,
    allDay: false,
    editorialPriority: 'minor',
    hemisphere: 'both',
  },
  {
    calendarEventId: 'cev_2026_09_new_moon',
    kind: 'lunar_phase',
    family: 'computed_astro',
    title: 'New moon',
    slug: 'new-moon-september-2026',
    summary: 'The dark of the moon — the busiest editorial day of the lunar month.',
    startsAt: '2026-09-11T03:27:00.000Z',
    endsAt: null,
    allDay: false,
    editorialPriority: 'notable',
    groupCode: 'new-moon-in-virgo',
    hemisphere: 'both',
  },
  {
    calendarEventId: 'cev_2026_09_first_quarter',
    kind: 'lunar_phase',
    family: 'computed_astro',
    title: 'First-quarter moon',
    slug: 'first-quarter-moon-september-2026',
    summary: 'The waxing half.',
    startsAt: '2026-09-18T15:44:00.000Z',
    endsAt: null,
    allDay: false,
    editorialPriority: 'minor',
    hemisphere: 'both',
  },
  {
    // The one hand-written kind. `all_day` is stored as a local-midnight instant PAIR, which is why
    // this row carries an `endsAt` and the four moon phases do not
    // (`calendar_event_allday_ck`, quoted at `calendar-write.service.ts`).
    calendarEventId: 'cev_2026_09_equinox_week',
    kind: 'editorial_moment',
    family: 'editorial',
    title: 'Equinox week',
    slug: 'equinox-week-2026',
    summary: 'A themed week the editors keep: balance, thresholds, turning points.',
    startsAt: '2026-09-21T00:00:00.000Z',
    endsAt: '2026-09-28T00:00:00.000Z',
    allDay: true,
    editorialPriority: 'major',
    hemisphere: 'both',
  },
  {
    calendarEventId: 'cev_2026_09_full_moon',
    kind: 'lunar_phase',
    family: 'computed_astro',
    title: 'Full moon',
    slug: 'full-moon-september-2026',
    summary: 'The harvest moon.',
    startsAt: '2026-09-26T16:49:00.000Z',
    endsAt: null,
    allDay: false,
    editorialPriority: 'notable',
    groupCode: 'full-moon-in-aries',
    hemisphere: 'both',
  },
];

/**
 * A few of the 185 groups migration 0124 seeds (design 30), in its order and with its English
 * names: two moon parents with one sign each, a retrograde, a season and two holidays. The two
 * moon events above name the two sign groups.
 */
const GROUPS: readonly CalendarGroup[] = [
  {
    code: 'new-moon',
    kind: 'lunar_phase',
    parentCode: null,
    name: 'New Moon',
    facts: { phase: 'new' },
  },
  {
    code: 'new-moon-in-virgo',
    kind: 'lunar_phase',
    parentCode: 'new-moon',
    name: 'New Moon in Virgo',
    facts: { phase: 'new', sign: 'virgo' },
  },
  {
    code: 'full-moon',
    kind: 'lunar_phase',
    parentCode: null,
    name: 'Full Moon',
    facts: { phase: 'full' },
  },
  {
    code: 'full-moon-in-aries',
    kind: 'lunar_phase',
    parentCode: 'full-moon',
    name: 'Full Moon in Aries',
    facts: { phase: 'full', sign: 'aries' },
  },
  {
    code: 'autumn-equinox',
    kind: 'solar_season',
    parentCode: null,
    name: 'Autumn Equinox',
    facts: {},
  },
  {
    code: 'mercury-retrograde',
    kind: 'retrograde',
    parentCode: null,
    name: 'Mercury Retrograde',
    facts: { body: 'mercury' },
  },
  { code: 'christmas', kind: 'holiday', parentCode: null, name: 'Christmas', facts: {} },
  { code: 'samhain', kind: 'holiday', parentCode: null, name: 'Samhain', facts: {} },
];

/**
 * How many pieces are scheduled or published per UTC day ACROSS ALL AUTHORS — counts only, and the
 * figure includes this author, "because a figure that silently excluded the reader would make two
 * authors comparing the same day see different numbers" (`authors-api.yaml`).
 *
 * The shape of this fixture is the shape of the real answer: crowded around the new moon, crowded
 * again in equinox week, quiet in between. That is the question a writer actually has.
 */
const DAY_LOAD: readonly AuthorCalendarDayLoad[] = [
  { day: '2026-09-01', scheduled: 1, byCategory: [{ category: 'ritualsPractice', scheduled: 1 }] },
  { day: '2026-09-03', scheduled: 2, byCategory: [{ category: 'moonAstrology', scheduled: 2 }] },
  { day: '2026-09-08', scheduled: 1, byCategory: [{ category: 'healingHealth', scheduled: 1 }] },
  {
    day: '2026-09-11',
    scheduled: 7,
    byCategory: [
      { category: 'moonAstrology', scheduled: 5 },
      { category: 'ritualsPractice', scheduled: 2 },
    ],
  },
  { day: '2026-09-12', scheduled: 3, byCategory: [{ category: 'moonAstrology', scheduled: 3 }] },
  { day: '2026-09-18', scheduled: 2, byCategory: [{ category: 'tarotDivination', scheduled: 2 }] },
  { day: '2026-09-21', scheduled: 4, byCategory: [{ category: 'moonAstrology', scheduled: 4 }] },
  { day: '2026-09-22', scheduled: 5, byCategory: [{ category: 'ritualsPractice', scheduled: 5 }] },
  { day: '2026-09-26', scheduled: 6, byCategory: [{ category: 'moonAstrology', scheduled: 6 }] },
  { day: '2026-09-30', scheduled: 1, byCategory: [{ category: 'healingHealth', scheduled: 1 }] },
];

/**
 * This author's OWN pieces in the range — the only titles on this wire.
 *
 * `goLiveAt` is set for a `scheduled` piece and `publishedAt` for a `published` one; both are
 * declared nullable, so a row may honestly carry neither. `status` is the ruled `ArticleStatus`
 * enum (`authors-api.yaml` ArticleStatus: draft, in_review, changes_requested, scheduled,
 * published, unpublished, archived) and never a free string.
 */
const ARTICLES: readonly AuthorCalendarArticle[] = [
  {
    articleId: 'art_new_moon_intentions',
    status: 'published',
    title: 'Six intentions to set on a September new moon',
    goLiveAt: '2026-09-11T06:00:00.000Z',
    publishedAt: '2026-09-11T06:00:12.000Z',
  },
  {
    articleId: 'art_equinox_thresholds',
    status: 'scheduled',
    title: 'Thresholds: what the equinox asks of a practice',
    goLiveAt: '2026-09-22T07:30:00.000Z',
    publishedAt: null,
  },
  {
    articleId: 'art_harvest_moon_letter',
    status: 'scheduled',
    title: 'A letter for the harvest moon',
    goLiveAt: '2026-09-26T18:00:00.000Z',
    publishedAt: null,
  },
];

/**
 * The publish-date suggestions, ordered as the server orders them: editorial priority first, THEN
 * proximity (`src/modules/editorial/cabinet/publish-date-suggestions.service.ts`). The order
 * below is deliberately NOT chronological, so the screen's `earliestSuggestedMonth` is exercised
 * against a list whose first item is not its earliest — which is the bug that derivation exists to
 * avoid.
 *
 * `categoryCodes` is NEVER SENT — the tailoring it described cannot be computed and emitting it
 * would assert a tailoring that did not happen (`authors-api.yaml`). The fixture omits it
 * for the same reason the server does.
 */
const SUGGESTIONS: PublishDateSuggestions = {
  items: [
    {
      date: '2026-09-21',
      reason: 'Equinox week — a themed week the editors keep',
      calendarEventId: 'cev_2026_09_equinox_week',
      editorialPriority: 'major',
    },
    {
      date: '2026-09-11',
      reason: 'New moon',
      calendarEventId: 'cev_2026_09_new_moon',
      editorialPriority: 'notable',
    },
    {
      date: '2026-09-26',
      reason: 'Full moon — the harvest moon',
      calendarEventId: 'cev_2026_09_full_moon',
      editorialPriority: 'notable',
    },
    {
      date: '2026-10-10',
      reason: 'New moon',
      calendarEventId: 'cev_2026_10_new_moon',
      editorialPriority: 'notable',
    },
  ],
};

export interface FixtureCalendarApiOptions {
  /**
   * Simulated latency. Non-zero on purpose: a loading state nobody ever sees is a loading state
   * nobody ever gets right — the reason admin's `features/queues/api/mock-transport.ts` gives.
   */
  readonly latencyMs?: number;
  /**
   * `empty` serves no events, no load, no articles and NO SUGGESTIONS — the state in which this
   * screen genuinely cannot choose a month and has to ask for one. It is reachable on purpose.
   */
  readonly scenario?: 'seeded' | 'empty';
}

function delay<T>(value: T, ms: number, signal?: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      resolve(value);
    }, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/** The 400 the service raises for an inverted or empty range. It carries NO domain code: "No DOMAIN
 *  code means *validation failed*" and the registered row answers the generic one
 *  (`author-calendar.service.ts`). */
function badRange(detail: string): ApiProblem {
  return new ApiProblem({
    type: 'about:blank',
    title: 'The range is not one this API will serve.',
    status: 400,
    error_code: 'REQUEST_VALIDATION_FAILED',
    detail,
  });
}

export function createFixtureCalendarApi(options: FixtureCalendarApiOptions = {}): CalendarApi {
  const latency = options.latencyMs ?? 120;
  const empty = options.scenario === 'empty';

  return {
    async getAuthorCalendar(query: OperationQuery<'getAuthorCalendar'>, signal?: AbortSignal) {
      const from = new Date(query.from);
      const to = new Date(query.to);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        throw badRange('both bounds must be instants.');
      }
      if (to.getTime() <= from.getTime()) {
        throw badRange(
          to.getTime() === from.getTime()
            ? "'to' is exclusive, so from == to is an empty range; widen it."
            : "'to' must be after 'from'.",
        );
      }

      const within = (instant: string): boolean => {
        const at = new Date(instant).getTime();
        return at >= from.getTime() && at < to.getTime();
      };

      const body: AuthorCalendar = {
        // ECHOED, NOT CLAMPED. The service refuses an over-long range rather than trimming it, so
        // "the clamped range and the requested range are the same thing by construction"
        // (`author-calendar.service.ts`). The screen prints these two and never its own.
        from: query.from,
        to: query.to,
        dayLoad: empty
          ? []
          : DAY_LOAD.filter((row) => within(`${row.day}T00:00:00.000Z`)).map((row) => ({ ...row })),
        events: empty
          ? []
          : EVENTS.filter((event) => within(event.startsAt)).map((e) => ({ ...e })),
        articles: empty
          ? []
          : ARTICLES.filter((article) => within(article.publishedAt ?? article.goLiveAt ?? '')).map(
              (a) => ({ ...a }),
            ),
      };
      return delay(body, latency, signal);
    },

    getPublishDateSuggestions(_query, signal?: AbortSignal) {
      return delay<PublishDateSuggestions>(
        empty ? { items: [] } : { items: SUGGESTIONS.items.map((item) => ({ ...item })) },
        latency,
        signal,
      );
    },

    // The groups are the catalogue, not the month, so even the `empty` scenario has them.
    listCalendarGroups(signal?: AbortSignal) {
      return delay<CalendarGroupList>(
        { items: GROUPS.map((group) => ({ ...group })) },
        latency,
        signal,
      );
    },
  };
}

/**
 * The seeded events, for the editor's fixture desk. An article's calendar links name these ids, so
 * a link the fixture editor accepts is an event this fixture calendar shows, and an id it does not
 * hold is the "names no live event" 404 (features/cabinet/api/fixture-editor-api.ts).
 */
export const FIXTURE_CALENDAR_EVENTS: readonly AuthorCalendarEvent[] = EVENTS;

/** The seeded groups, for the editor's fixture desk: a topic it accepts is one this list offers. */
export const FIXTURE_CALENDAR_GROUPS: readonly CalendarGroup[] = GROUPS;
